// IEA Site Survey, step 3: Map (pass 1). Pure: data in, answers out.
// Words: IEA_STEP3_MAP_WORDS.md (approved 2026-10-15). Mockup: MOCKUP_IEA_NODE_MAP.html v7 (approved).
// Design: IEA_SURVEY_DESIGN.md section 11. Data: IEA_WALK_SPEC.md 4.1, changed by Map words section 6.
// Unit tested: tests/unit/survey-map.test.ts.
//
// Only Connections rows are saved. Rooms, the bus links from a housing to its positions, Fed From
// and Feeds, the layout and the gap list are figured every time. The survey never creates an asset.

import { housingOf, isUtility, type PackAsset, type PackLink } from "./survey";

// ---- Words ----

export type How = "Seen" | "Told" | "Assumed";
export const HOWS: readonly How[] = ["Seen", "Told", "Assumed"];
export const nextHow = (h: How): How => HOWS[(HOWS.indexOf(h) + 1) % HOWS.length]!;

/** To Point words that mean "the line side of the next asset", not a position. */
// "Normal" stays only so transfer switch records saved before 2026-10-30 still read as the Primary side.
const LINE_SIDE = new Set(["", "Main", "Bus", "Primary", "Normal"]);

/** A To Point naming a position: a bucket fed by its own conductors (Fed Separately). */
export function isPositionPoint(toPoint: string): boolean {
  return !LINE_SIDE.has(toPoint) && toPoint !== "Emergency";
}

/** Housings that take a typed breaker or circuit number when the registry has no positions. */
const BREAKER_HOUSINGS = new Set(["PNL", "SWBD", "SWGR", "MCC", "LP", "DP", "PP", "MDP", "MSB", "SB", "LC"]);
/** Transfer switches: the first feed is Primary, a second source is Emergency. Never N or Normal: N means neutral
 *  (Robert 2026-10-30 8:04 AM). Old records saved as "Normal" still read as the Primary side. */
const TRANSFER = new Set(["ATS", "MTS"]);

/** The Connections columns in order (IEA_WALK_SPEC.md 4.1, plus How Known, plus Holder Name). */
export const COLUMNS = [
  "Connection ID", "Site Number", "From Asset", "From Point", "To Asset", "To Point", "Goes To", "Link Type",
  "Conductor Size", "Conductor Material", "Insulation Type", "Conductors per Phase", "Raceway Type", "Raceway Size",
  "Raceway Material", "Cable Construction", "Busway Amp Rating", "Est. Length (ft)", "Not Visible Fields",
  "End Load Type", "End Load Tag", "End Load HP", "Notes", "Status", "Recorded By", "Holder Name", "Recorded On",
  "Recorded At", "How Known",
] as const;

// ---- Assets ----

/** Class code: the registry's, or read from the ID (01-ER2-MCC-1A is MCC, 01-UTL-01 is UTL). */
export function codeOf(a: Pick<PackAsset, "assetId" | "equipmentCode">): string {
  if (a.equipmentCode) return a.equipmentCode.toUpperCase();
  const p = a.assetId.split("-");
  return (/^(ER|EA|EL)\d+[a-z]?$/i.test(p[1] ?? "") ? p[2] : p[1])?.toUpperCase() ?? "";
}

export function isSource(a: PackAsset): boolean {
  return isUtility(a) || codeOf(a) === "GEN" || /^generator$/i.test(a.equipmentType.trim());
}

export interface Place {
  /** ER1, EA1, EL1, ER3a. Null outside (sources) or when it cannot be told. */
  room: string | null;
  /** Gear fed from the room but sitting elsewhere: the Floor tab of that room. */
  extended: boolean;
}

const ROOM_CODE = /^(ER|EA|EL)\d+[a-z]?$/i;

/**
 * The room an asset hangs from, from its location ("Electrical Room: ER1", "Electrical
 * Room-Extended: ER1"). A location with no number falls back to the room in the Asset ID, which
 * is the source room by the naming rule.
 */
export function placeOf(a: Pick<PackAsset, "assetId" | "location">): Place {
  const loc = a.location.trim();
  if (!/^Electrical (Room|Area|Location)/i.test(loc)) return { room: null, extended: false };
  const extended = /-\s*Extended/i.test(loc);
  const m = /:\s*([A-Za-z]{2}\d+[a-z]?)\s*$/.exec(loc);
  if (m && ROOM_CODE.test(m[1]!)) return { room: m[1]!, extended };
  const fromId = a.assetId.split("-")[1] ?? "";
  return { room: ROOM_CODE.test(fromId) ? fromId : null, extended };
}

/** "Electrical Room ER2". */
export function roomWords(room: string): string {
  const k = room.slice(0, 2).toUpperCase();
  return `${k === "EA" ? "Electrical Area" : k === "EL" ? "Electrical Location" : "Electrical Room"} ${room}`;
}

/** The name on the yellow label: MCC-1A, (ER2) DS-04, UTL-01. The full ID shows on tap. */
export function shortName(a: Pick<PackAsset, "assetId" | "location">): string {
  const p = a.assetId.split("-");
  const place = placeOf(a);
  if (ROOM_CODE.test(p[1] ?? "")) {
    const rest = p.slice(2).join("-");
    return place.extended ? `(${p[1]}) ${rest}` : rest;
  }
  return p.slice(1).join("-");
}

/** The site's registry, indexed once for the Map. */
export class Registry {
  readonly byId: Map<string, PackAsset>;
  readonly ids: Set<string>;
  private readonly positions = new Map<string, string[]>();

  constructor(readonly assets: readonly PackAsset[]) {
    this.byId = new Map(assets.map((a) => [a.assetId, a]));
    this.ids = new Set(this.byId.keys());
    for (const a of assets) {
      const h = housingOf(a.assetId, this.ids);
      if (h) this.positions.set(h, [...(this.positions.get(h) ?? []), a.assetId.slice(h.length + 1)]);
    }
  }

  /** Position codes of a housing, in registry order (A1, A2, B1). */
  positionsOf(assetId: string): string[] {
    return this.positions.get(assetId) ?? [];
  }

  isPosition(assetId: string): boolean {
    return housingOf(assetId, this.ids) !== null;
  }

  /** What comes next after this asset: its positions, a typed breaker, or straight out. */
  wayOut(assetId: string): "positions" | "breaker" | "out" {
    const a = this.byId.get(assetId);
    if (!a) return "out";
    if (this.positionsOf(assetId).length) return "positions";
    return BREAKER_HOUSINGS.has(codeOf(a)) ? "breaker" : "out";
  }

  /** Panels and switchboards offer Feed-Through off the lugs. */
  hasLugs(assetId: string): boolean {
    const a = this.byId.get(assetId);
    return Boolean(a) && BREAKER_HOUSINGS.has(codeOf(a!)) && codeOf(a!) !== "MCC";
  }

  sources(): PackAsset[] {
    return this.assets.filter((a) => isSource(a) && !this.isPosition(a.assetId));
  }

  /** Rooms, areas and locations of the site, in order. */
  rooms(): string[] {
    const set = new Set<string>();
    for (const a of this.assets) {
      const r = placeOf(a).room;
      if (r) set.add(r);
    }
    return [...set].sort((x, y) => x.localeCompare(y, undefined, { numeric: true }));
  }

  /** Assets that hang on the tree: not a position, not a source. */
  parents(): PackAsset[] {
    return this.assets.filter((a) => !isSource(a) && !this.isPosition(a.assetId));
  }

  /** A room's parent assets. Floor: the extended ones fed from it. */
  roomParents(room: string, floor: boolean): PackAsset[] {
    return this.parents().filter((a) => {
      const p = placeOf(a);
      return p.room === room && p.extended === floor;
    });
  }

  /**
   * IDs that look like a position (site-room-code-number-position) but have no housing row in the
   * registry (A-17: 02-ER1-MCC-1B has 46 buckets and no MCC row). Map cannot place them.
   */
  orphanPositions(): PackAsset[] {
    return this.parents().filter((a) => {
      const p = a.assetId.split("-");
      return ROOM_CODE.test(p[1] ?? "") && p.length >= 5 && codeOf(a) === "MCC";
    });
  }
}

// ---- Connections ----

/** One Connections row as the Map reads it. */
export interface MapLink {
  id: string;
  fromAsset: string;
  fromPoint: string;
  toAsset: string;
  toPoint: string;
  goesTo: string;
  howKnown: How;
  status: string;
  endLoadType: string;
  endLoadHp: string;
  endLoadTag: string;
  notes: string;
  /** Order the links were made in. Recorded At. */
  at: string;
}

export function linkFromPack(l: PackLink): MapLink {
  const how = (HOWS as readonly string[]).includes(l.howKnown ?? "") ? (l.howKnown as How) : "Seen";
  return {
    id: l.id ?? connectionId(l.fromAsset, l.fromPoint ?? "", l.toAsset, 1),
    fromAsset: l.fromAsset,
    fromPoint: l.fromPoint ?? "",
    toAsset: l.toAsset,
    toPoint: l.toPoint ?? "",
    goesTo: l.goesTo,
    howKnown: how,
    status: l.status || "Active",
    endLoadType: l.endLoadType ?? "",
    endLoadHp: l.endLoadHp ?? "",
    endLoadTag: l.endLoadTag ?? "",
    notes: l.notes ?? "",
    at: l.recordedAt ?? "",
  };
}

/** Fixed per link: From Asset + From Point + To Asset, or the load number for a load. */
export function connectionId(fromAsset: string, fromPoint: string, toAsset: string, loadNumber: number): string {
  return `${fromAsset}__${fromPoint}__${toAsset || `L${loadNumber}`}`;
}

/** The next load number under one way out. Removed loads keep their number, so none is reused. */
export function nextLoadNumber(links: readonly MapLink[], fromAsset: string, fromPoint: string): number {
  const head = `${fromAsset}__${fromPoint}__L`;
  let max = 0;
  for (const l of links) if (l.id.startsWith(head)) max = Math.max(max, Number(l.id.slice(head.length)) || 0);
  return max + 1;
}

/** A typed breaker or circuit number, cleaned to what the rules take. Empty when unusable. */
export function cleanPoint(raw: string): string {
  const v = raw.trim().toUpperCase().replace(/\s+/g, "-").replace(/[^A-Z0-9-]/g, "");
  return /^[A-Z0-9][A-Z0-9-]{0,11}$/.test(v) ? v : "";
}

/** The point an asset feeds out of when it has no positions: Sec for a utility, Out otherwise. */
export function outPoint(a: PackAsset): string {
  return isUtility(a) ? "Sec" : "Out";
}

export interface Stamp {
  siteNumber: string;
  badgeId: string;
  holderName: string;
  deviceName: string;
  at: string;
}

export interface NewLink {
  fromAsset: string;
  fromPoint: string;
  toAsset: string;
  toPoint: string;
  howKnown: How;
  load?: { type: "Motor" | "Non-motor"; hp: string; tag: string; description: string };
}

/** A whole new row. Wire fields stay blank: Collect fills the same row (D23). */
export function newRow(link: NewLink, id: string, s: Stamp): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const c of COLUMNS) row[c] = "";
  return {
    ...row,
    "Connection ID": id,
    "Site Number": s.siteNumber,
    "From Asset": link.fromAsset,
    "From Point": link.fromPoint,
    "To Asset": link.load ? "" : link.toAsset,
    "To Point": link.load ? "" : link.toPoint,
    "Goes To": link.load ? "End load" : "Equipment",
    "End Load Type": link.load?.type ?? "",
    "End Load HP": link.load?.hp.trim() ?? "",
    "End Load Tag": link.load?.tag.trim() ?? "",
    Notes: link.load?.description.trim() ?? "",
    Status: "Active",
    "Recorded By": s.badgeId,
    "Holder Name": s.holderName,
    "Recorded On": s.deviceName,
    "Recorded At": s.at,
    "How Known": link.howKnown,
  };
}

/** The link fields the Map owns. Collect owns the wire fields; a Map edit never resends them. */
export const MAP_FIELDS = [
  "Connection ID", "Site Number", "From Asset", "From Point", "To Asset", "To Point", "Goes To", "End Load Type",
  "End Load Tag", "End Load HP", "Notes", "Status", "Recorded By", "Holder Name", "Recorded On", "Recorded At", "How Known",
] as const;

/**
 * What the device list keeps for an edit. Not yet sent: the whole row, changed (the create has not
 * reached the cloud). Already sent: the Map's own fields, changed, so the iPad and a backup still know
 * the link, and the cloud's wire fields are never blanked.
 */
export function edited(previous: Record<string, unknown> | null, sent: boolean, changes: Record<string, unknown>): Record<string, unknown> {
  if (!previous) return changes;
  if (!sent) return { ...previous, ...changes };
  const kept: Record<string, unknown> = {};
  for (const k of MAP_FIELDS) if (k in previous) kept[k] = previous[k];
  // Collect's wire, already sent from this iPad, stays known here. A blank is never kept, so it never
  // overwrites a wire another device filled (Collect words section 7).
  for (const k of WIRE_FIELDS) if (previous[k] !== undefined && previous[k] !== "") kept[k] = previous[k];
  return { ...kept, ...changes };
}

/** The wire fields Collect fills on the Map's row (D23). */
export const WIRE_FIELDS = ["Link Type", "Conductor Size", "Conductors per Phase", "Conductor Material", "Insulation Type", "Raceway Type", "Est. Length (ft)", "Busway Amp Rating"] as const;

/** The fields an edit sends. Merged into the cloud row, so Collect's wire fields stay. */
export function editRow(id: string, changes: Record<string, unknown>, s: Stamp): Record<string, unknown> {
  return { ...changes, "Connection ID": id, "Site Number": s.siteNumber, "Recorded By": s.badgeId, "Holder Name": s.holderName, "Recorded On": s.deviceName, "Recorded At": s.at };
}

/** A row read back into the Map's own form. */
export function linkFromRow(data: Record<string, unknown>): MapLink {
  const g = (k: string) => String(data[k] ?? "");
  return linkFromPack({
    id: g("Connection ID"),
    fromAsset: g("From Asset"),
    fromPoint: g("From Point"),
    toAsset: g("To Asset"),
    toPoint: g("To Point"),
    goesTo: g("Goes To"),
    status: g("Status"),
    howKnown: g("How Known"),
    endLoadType: g("End Load Type"),
    endLoadHp: g("End Load HP"),
    endLoadTag: g("End Load Tag"),
    notes: g("Notes"),
    recordedAt: g("Recorded At"),
  });
}

/** Lays local saves over the pack's rows: a saved edit wins over the downloaded copy. */
export function mergeLinks(base: readonly MapLink[], local: readonly Record<string, unknown>[]): MapLink[] {
  const by = new Map(base.map((l) => [l.id, l]));
  for (const data of local) {
    const id = String(data["Connection ID"] ?? "");
    if (!id) continue;
    const prev = by.get(id);
    const merged = prev
      ? linkFromRow({
          "Connection ID": prev.id, "From Asset": prev.fromAsset, "From Point": prev.fromPoint, "To Asset": prev.toAsset, "To Point": prev.toPoint,
          "Goes To": prev.goesTo, Status: prev.status, "How Known": prev.howKnown, "End Load Type": prev.endLoadType, "End Load HP": prev.endLoadHp,
          "End Load Tag": prev.endLoadTag, Notes: prev.notes, "Recorded At": prev.at, ...data,
        })
      : linkFromRow(data);
    by.set(id, merged);
  }
  return [...by.values()];
}

export const active = (links: readonly MapLink[]) => links.filter((l) => l.status !== "Removed");

/** Assets with their one feed on the tree. A second source or a bucket fed separately does not count. */
export function placedSet(links: readonly MapLink[]): Set<string> {
  const s = new Set<string>();
  for (const l of active(links)) if (l.goesTo === "Equipment" && l.toAsset && !isPositionPoint(l.toPoint) && l.toPoint !== "Emergency") s.add(l.toAsset);
  return s;
}

/** The To Point a first feed gets: Primary into a transfer switch, otherwise blank until Collect. */
export function firstToPoint(reg: Registry, toAsset: string): string {
  const a = reg.byId.get(toAsset);
  return a && TRANSFER.has(codeOf(a)) ? "Primary" : "";
}

export function isTransfer(reg: Registry, assetId: string): boolean {
  const a = reg.byId.get(assetId);
  return Boolean(a) && TRANSFER.has(codeOf(a!));
}

// ---- The tree (figured) ----

export type NodeKind = "source" | "room" | "asset" | "point" | "load";

export interface MapNode {
  key: string;
  kind: NodeKind;
  parent: string | null;
  /** Asset ID for source and asset; room code; point code; Connection ID for a load. */
  ref: string;
  /** The asset a point sits on. */
  asset: string;
  room: string | null;
  /** The saved link that draws the line into this node, when there is one. */
  linkId: string;
  how: How;
  floor: boolean;
  draft: boolean;
}

export interface SideLine {
  from: string;
  to: string;
  kind: "second" | "separate";
  linkId: string;
  how: How;
}

export interface Draft {
  kind: "source" | "room" | "point";
  parent: string | null;
  ref: string;
  asset: string;
}

export interface MapTree {
  nodes: Map<string, MapNode>;
  kids: Map<string, string[]>;
  roots: string[];
  sides: SideLine[];
}

export const sourceKey = (id: string) => `s:${id}`;
export const assetKey = (id: string) => `a:${id}`;
export const pointKey = (id: string, point: string) => `p:${id}:${point}`;
export const roomKey = (up: string, room: string) => `r:${up}|${room}`;
export const loadKey = (linkId: string) => `l:${linkId}`;

/** The key of the node a link leaves from: the asset itself for Out, Sec or Lugs, else its point. */
export function upKey(reg: Registry, fromAsset: string, fromPoint: string): string {
  const a = reg.byId.get(fromAsset);
  const self = a && isSource(a) ? sourceKey(fromAsset) : assetKey(fromAsset);
  return fromPoint === "Out" || fromPoint === "Sec" ? self : pointKey(fromAsset, fromPoint);
}

/** The room a node sits in, for the rule "a new room gets a room node". */
export function roomOfNode(t: MapTree, key: string | null): string | null {
  let k = key;
  while (k) {
    const n = t.nodes.get(k);
    if (!n) return null;
    if (n.kind === "source") return null;
    if (n.room) return n.room;
    k = n.parent;
  }
  return null;
}

export function buildTree(reg: Registry, links: readonly MapLink[], drafts: readonly Draft[]): MapTree {
  const nodes = new Map<string, MapNode>();
  const order: string[] = [];
  const sides: SideLine[] = [];
  const add = (n: MapNode): MapNode => {
    const have = nodes.get(n.key);
    if (have) return have;
    nodes.set(n.key, n);
    order.push(n.key);
    return n;
  };
  const base = { linkId: "", how: "Seen" as How, floor: false, draft: false };

  /** The node a link leaves from, made with what it hangs on. Assets not yet placed float as roots. */
  const ensureUp = (fromAsset: string, fromPoint: string): string => {
    const a = reg.byId.get(fromAsset);
    if (a && isSource(a)) {
      add({ ...base, key: sourceKey(fromAsset), kind: "source", parent: null, ref: fromAsset, asset: fromAsset, room: null });
    } else {
      add({ ...base, key: assetKey(fromAsset), kind: "asset", parent: null, ref: fromAsset, asset: fromAsset, room: a ? placeOf(a).room : null, floor: a ? placeOf(a).extended : false });
    }
    const up = upKey(reg, fromAsset, fromPoint);
    if (up.startsWith("p:")) add({ ...base, key: up, kind: "point", parent: assetKey(fromAsset), ref: fromPoint, asset: fromAsset, room: null });
    return up;
  };

  const live = active(links).slice().sort((x, y) => x.at.localeCompare(y.at));
  // Drafts first so a room picked before its first asset keeps its place.
  for (const d of drafts) {
    if (d.kind === "source") add({ ...base, key: sourceKey(d.ref), kind: "source", parent: null, ref: d.ref, asset: d.ref, room: null, draft: true });
  }
  for (const l of live) {
    if (l.goesTo !== "Equipment" || isPositionPoint(l.toPoint) || l.toPoint === "Emergency") continue;
    ensureUp(l.fromAsset, l.fromPoint);
  }

  // Place each asset under what feeds it, through a room node when it changes room.
  for (const l of live) {
    if (l.goesTo === "Equipment" && l.toAsset && !isPositionPoint(l.toPoint) && l.toPoint !== "Emergency") {
      const up = ensureUp(l.fromAsset, l.fromPoint);
      const a = reg.byId.get(l.toAsset);
      const place = a ? placeOf(a) : { room: null, extended: false };
      const upRoom = roomOfNode({ nodes, kids: new Map(), roots: [], sides }, up);
      let parent = up;
      if (place.room && place.room !== upRoom) {
        parent = roomKey(up, place.room);
        add({ ...base, key: parent, kind: "room", parent: up, ref: place.room, asset: "", room: place.room });
      }
      const key = assetKey(l.toAsset);
      const existing = nodes.get(key);
      const node: MapNode = { ...base, key, kind: "asset", parent, ref: l.toAsset, asset: l.toAsset, room: place.room, floor: place.extended, linkId: l.id, how: l.howKnown };
      if (existing) Object.assign(existing, { parent, linkId: l.id, how: l.howKnown, draft: false });
      else add(node);
    }
  }
  for (const l of live) {
    if (l.goesTo === "End load") {
      const up = ensureUp(l.fromAsset, l.fromPoint);
      add({ ...base, key: loadKey(l.id), kind: "load", parent: up, ref: l.id, asset: "", room: null, linkId: l.id, how: l.howKnown });
    }
  }
  // Drafts: a position or breaker picked, or a room picked, with nothing saved below it yet.
  for (const d of drafts) {
    if (d.kind === "point" && nodes.has(assetKey(d.asset))) add({ ...base, key: pointKey(d.asset, d.ref), kind: "point", parent: assetKey(d.asset), ref: d.ref, asset: d.asset, room: null, draft: true });
  }
  for (const d of drafts) {
    if (d.kind === "room" && d.parent && nodes.has(d.parent)) add({ ...base, key: roomKey(d.parent, d.ref), kind: "room", parent: d.parent, ref: d.ref, asset: "", room: d.ref, draft: true });
  }
  // Side lines: a second source into a transfer switch, a bucket fed by its own conductors.
  for (const l of live) {
    if (l.goesTo !== "Equipment" || !l.toAsset) continue;
    if (l.toPoint === "Emergency") {
      const from = ensureUp(l.fromAsset, l.fromPoint);
      sides.push({ from, to: assetKey(l.toAsset), kind: "second", linkId: l.id, how: l.howKnown });
    } else if (isPositionPoint(l.toPoint)) {
      const from = ensureUp(l.fromAsset, l.fromPoint);
      const to = pointKey(l.toAsset, l.toPoint);
      if (nodes.has(assetKey(l.toAsset))) add({ ...base, key: to, kind: "point", parent: assetKey(l.toAsset), ref: l.toPoint, asset: l.toAsset, room: null });
      sides.push({ from, to, kind: "separate", linkId: l.id, how: l.howKnown });
    }
  }

  const kids = new Map<string, string[]>();
  const roots: string[] = [];
  for (const k of order) {
    const n = nodes.get(k)!;
    if (n.parent && nodes.has(n.parent)) kids.set(n.parent, [...(kids.get(n.parent) ?? []), k]);
    else {
      n.parent = null;
      roots.push(k);
    }
  }
  // Positions in registry order on the gear.
  for (const [k, list] of kids) {
    const n = nodes.get(k)!;
    if (n.kind !== "asset") continue;
    const reg0 = reg.positionsOf(n.ref);
    const rank = (key: string) => {
      const c = nodes.get(key)!;
      if (c.kind !== "point") return Number.MAX_SAFE_INTEGER;
      const i = reg0.indexOf(c.ref);
      return i < 0 ? reg0.length : i;
    };
    list.sort((x, y) => rank(x) - rank(y));
  }
  // Sources first, then anything floating (a placed asset whose feed was removed).
  roots.sort((x, y) => Number(nodes.get(y)!.kind === "source") - Number(nodes.get(x)!.kind === "source"));
  return { nodes, kids, roots, sides };
}

export function pathTo(t: MapTree, key: string): MapNode[] {
  const out: MapNode[] = [];
  let k: string | null = key;
  while (k) {
    const n = t.nodes.get(k);
    if (!n) break;
    out.unshift(n);
    k = n.parent;
  }
  return out;
}

export function countBelow(t: MapTree, key: string): number {
  let c = 0;
  for (const k of t.kids.get(key) ?? []) c += 1 + countBelow(t, k);
  return c;
}

/**
 * Remove one asset and keep what is below it: each feed out of it re-hangs on the line above
 * (UTL-01, EA2, MD-01, MSD-01: remove MD-01 and MSD-01 moves up under EA2). Only for an asset
 * that feeds straight out. A housing with positions or breakers below is cleared from the bottom
 * up. Null when it cannot be spliced.
 */
/**
 * Undo on Map Remove (CANCEL_WORDS.md section 3): each removed link back to the status it had, and the relinks the
 * remove made taken out. A relink that reused a removed link's ID comes back with it.
 */
export function undoRemove(links: readonly { id: string; status: string }[], removed: readonly string[], added: readonly string[]): { id: string; status: string }[] {
  const back = removed.map((id) => ({ id, status: links.find((l) => l.id === id)?.status || "Active" }));
  return [...back, ...added.filter((id) => !removed.includes(id)).map((id) => ({ id, status: "Removed" }))];
}

export function spliceOut(reg: Registry, t: MapTree, links: readonly MapLink[], key: string): { relink: NewLink[]; remove: string[] } | null {
  const n = t.nodes.get(key);
  if (!n || n.kind !== "asset" || !n.linkId || reg.wayOut(n.ref) !== "out") return null;
  const into = links.find((l) => l.id === n.linkId);
  if (!into) return null;
  const outs = active(links).filter((l) => l.fromAsset === n.ref && (l.fromPoint === "Out" || l.fromPoint === "Sec"));
  const relink: NewLink[] = outs.map((o) => ({
    fromAsset: into.fromAsset,
    fromPoint: into.fromPoint,
    toAsset: o.goesTo === "End load" ? "" : o.toAsset,
    toPoint: o.goesTo === "End load" ? "" : o.toPoint,
    howKnown: o.howKnown,
    load: o.goesTo === "End load" ? { type: o.endLoadType === "Non-motor" ? "Non-motor" : "Motor", hp: o.endLoadHp, tag: o.endLoadTag, description: o.notes } : undefined,
  }));
  return { relink, remove: [into.id, ...outs.map((o) => o.id)] };
}

// ---- Node words ----

export function nodeLabel(reg: Registry, n: MapNode, links: readonly MapLink[]): string {
  if (n.kind === "room" || n.kind === "point") return n.ref;
  if (n.kind === "load") {
    const l = links.find((x) => x.id === n.ref);
    const what = l?.endLoadType === "Motor" ? "Motor" : "Load";
    return l?.endLoadHp ? `${what} ${l.endLoadHp} HP` : what;
  }
  const a = reg.byId.get(n.ref);
  return a ? shortName(a) : n.ref;
}

export function nodeSub(reg: Registry, n: MapNode, links: readonly MapLink[]): string {
  if (n.kind === "source") return reg.byId.get(n.ref)?.equipmentType || "Source";
  if (n.kind === "room") return roomWords(n.ref);
  if (n.kind === "point") return `${shortName(reg.byId.get(n.asset) ?? { assetId: n.asset, location: "" })} position`;
  if (n.kind === "load") {
    const l = links.find((x) => x.id === n.ref);
    return [l?.notes ? `"${l.notes}" as found` : "Load", l?.endLoadTag ? `Equip ${l.endLoadTag}` : ""].filter(Boolean).join(" · ");
  }
  const a = reg.byId.get(n.ref);
  if (!a) return "";
  const p = placeOf(a);
  return `${a.equipmentType || codeOf(a)}${p.room ? (p.extended ? ` · Floor, from ${p.room}` : ` · ${p.room}`) : ""}`;
}

/** The full ID a tap shows. */
export function fullId(siteNumber: string, n: MapNode): string {
  if (n.kind === "room") return roomWords(n.ref);
  if (n.kind === "point") return `${n.asset}-${n.ref}`;
  if (n.kind === "load") return "Load · no QR";
  return n.ref.startsWith(`${siteNumber}-`) ? n.ref : `${siteNumber}-${n.ref}`;
}

// ---- What the picker shows (Map words section 2) ----

export type Step =
  | { kind: "sources" }
  | { kind: "rooms" }
  | { kind: "roomAssets"; room: string }
  | { kind: "positions"; asset: string }
  | { kind: "breaker"; asset: string; lugs: boolean }
  | { kind: "feeds"; room: string | null; lugs: boolean };

/** The next dropdown after the selected node. Null at a load: end of chain. */
export function nextStep(reg: Registry, t: MapTree, key: string | null): Step | null {
  if (!key) return { kind: "sources" };
  const n = t.nodes.get(key);
  if (!n) return { kind: "sources" };
  if (n.kind === "load") return null;
  if (n.kind === "source") return { kind: "rooms" };
  if (n.kind === "room") return { kind: "roomAssets", room: n.ref };
  if (n.kind === "asset") {
    const w = reg.wayOut(n.ref);
    if (w === "positions") return { kind: "positions", asset: n.ref };
    if (w === "breaker") return { kind: "breaker", asset: n.ref, lugs: reg.hasLugs(n.ref) };
  }
  return { kind: "feeds", room: roomOfNode(t, key), lugs: false };
}

export function nextQuestion(reg: Registry, t: MapTree, key: string | null, links: readonly MapLink[]): string {
  const step = nextStep(reg, t, key);
  const n = key ? t.nodes.get(key) : undefined;
  const name = n ? nodeLabel(reg, n, links) : "";
  if (!step) return "";
  if (step.kind === "sources") return "start at a source";
  if (step.kind === "rooms") return `which room does ${name} feed?`;
  if (step.kind === "roomAssets") return `which asset in ${step.room}?`;
  if (step.kind === "positions") return `which position in ${name}?`;
  if (step.kind === "breaker") return `breaker or circuit no. in ${name}?`;
  return `what does ${name} feed?`;
}

/** The point a link out of this node leaves from, and the asset it leaves. */
export function linkStart(reg: Registry, t: MapTree, key: string): { fromAsset: string; fromPoint: string } | null {
  let n = t.nodes.get(key);
  // A room node passes the feed from the point above it.
  while (n && n.kind === "room") n = n.parent ? t.nodes.get(n.parent) : undefined;
  if (!n || n.kind === "load") return null;
  if (n.kind === "point") return { fromAsset: n.asset, fromPoint: n.ref };
  const a = reg.byId.get(n.ref);
  if (!a) return null;
  return { fromAsset: n.ref, fromPoint: outPoint(a) };
}

/** Open feeds in a room for Pick What Feeds It and Fed Separately: points and feed-out assets with nothing below. */
export function openFeeds(reg: Registry, t: MapTree, room: string, except: string): MapNode[] {
  const out: MapNode[] = [];
  for (const n of t.nodes.values()) {
    if (n.ref === except || n.asset === except) continue;
    if ((t.kids.get(n.key) ?? []).length) continue;
    if (n.kind === "point" && roomOfNode(t, n.key) === room) out.push(n);
    if (n.kind === "asset" && reg.wayOut(n.ref) === "out" && placeOf(reg.byId.get(n.ref) ?? { assetId: n.ref, location: "" }).room === room) out.push(n);
    if (n.kind === "room" && n.ref === room) out.push(n);
  }
  // Positions of a placed housing in the room that are not on the tree yet.
  for (const n of t.nodes.values()) {
    if (n.kind !== "asset" || n.ref === except || roomOfNode(t, n.key) !== room) continue;
    for (const p of reg.positionsOf(n.ref)) {
      if (!t.nodes.has(pointKey(n.ref, p))) out.push({ key: pointKey(n.ref, p), kind: "point", parent: n.key, ref: p, asset: n.ref, room: null, linkId: "", how: "Seen", floor: false, draft: true });
    }
  }
  return out;
}

// ---- The gap list ----

export interface Gaps {
  /** Parent assets with no feed mapped, by room. */
  byRoom: { room: string; assets: PackAsset[] }[];
  noRoom: PackAsset[];
  orphans: PackAsset[];
  total: number;
  onTree: number;
  of: number;
}

export function gaps(reg: Registry, links: readonly MapLink[]): Gaps {
  const placed = placedSet(links);
  const orphans = new Set(reg.orphanPositions().map((a) => a.assetId));
  const parents = reg.parents().filter((a) => !a.isOutOfService && !orphans.has(a.assetId));
  const open = parents.filter((a) => !placed.has(a.assetId));
  const by = new Map<string, PackAsset[]>();
  const noRoom: PackAsset[] = [];
  for (const a of open) {
    const r = placeOf(a).room;
    if (r) by.set(r, [...(by.get(r) ?? []), a]);
    else noRoom.push(a);
  }
  return {
    byRoom: reg.rooms().map((room) => ({ room, assets: by.get(room) ?? [] })),
    noRoom,
    orphans: reg.assets.filter((a) => orphans.has(a.assetId)),
    total: open.length,
    onTree: parents.length - open.length,
    of: parents.length,
  };
}

// ---- Recent (this device only) ----

export function pushRecent(recent: readonly string[], key: string, keep = 4): string[] {
  return [key, ...recent.filter((k) => k !== key)].slice(0, keep);
}

